import { useQuery, ApolloError } from "@apollo/client";
import { createContext, useState, useReducer, useEffect } from "react";
import useLocalStorageState from "use-local-storage-state";
import { GET_LINJER_IDS, GET_PAGED_LINJE_QUERY } from "../api/linje/queries";
import { useTableSelection } from "../components/Table/useTableSelection";
import { defaultLinjeColumns } from "../config/linjeConfig";
import useActiveColumns from "../hooks/useActiveColumns";
import { QueryFilter, LinjeFilterKeys } from "../interfaces/filters";
import { PagedLinjeContext } from "../interfaces/pagedContext";
import { queryFilterReducer } from "./reducers/queryFilterReducer";
import { buildFilter } from "../util/queryBuilder";
import { getActiveColumnFields } from "../util/table";
import {
  LinjeFiltersContext,
  LinjeFiltersEventContext,
} from "./FiltersContext";
import {
  PagedLinjeQuery,
  PagedLinjeQueryField,
  PagedLinjeResponse,
} from "../interfaces/linje";
import { IColumn, IRowData, SortBy } from "../interfaces/table";
import { linjeIndexSortOrderKey } from "../util/localStorageKeys";

export const PagedLinjeQueryContext = createContext<PagedLinjeContext>({
  loading: false,
  error: undefined,
  columns: [],
  setActiveColumns: () => {},
  setQuery: () => {},
  page: 1,
  pagedResponse: undefined,
  nextPage: undefined,
  prevPage: undefined,
  refetch: undefined,
  selectAllError: undefined,
  clearSelectAllError: () => {},
  selection: {
    selectedIds: [],
    addSelectedIds: () => {},
    clearSelection: () => {},
    removeSelectedIds: () => {},
    selectAll: () => {},
    lastSelectedId: undefined,
    setLastSelectedId: () => {},
  },
});

export const PagedLinjeQueryProvider = ({ children }) => {
  // type
  const type = "linje";

  // state
  const [fetchAllSelected, setFetchAllSelected] = useState(false);
  const [selectAllTotalCount, setSelectAllTotalCount] = useState<number | null>(
    null,
  );
  const [selectAllError, setSelectAllError] = useState<ApolloError | undefined>(
    undefined,
  );
  const [query, setQuery] = useState<PagedLinjeQuery>({
    page: 1,
  });

  // localstorage state
  const [storedFilters, setStoredFilters] = useLocalStorageState<
    QueryFilter<LinjeFilterKeys>[]
  >("linjeFilters", { defaultValue: [] });

  const [sortBy] = useLocalStorageState<Array<SortBy>>(linjeIndexSortOrderKey, {
    defaultValue: defaultSortBy,
  });

  // reducers
  const [queryFilters, updateFilters] = useReducer(
    queryFilterReducer,
    storedFilters,
  );

  // business
  const [columns, setActiveColumns] = useActiveColumns(
    "linjeindex-active-columns",
    defaultLinjeColumns,
  );

  const {
    addSelectedIds,
    clearSelection,
    removeSelectedIds,
    selectAll,
    selectedIds,
    lastSelectedId,
    setLastSelectedId,
  } = useTableSelection();

  const page = query.page ? query.page : 1;
  const { after, before } = query;

  const {
    data,
    error: selectAllQueryError,
    loading: fetchAllLoading,
  } = useQuery<PagedLinjeResponse>(
    GET_LINJER_IDS(query, selectAllTotalCount ?? 0),
    {
      skip: !fetchAllSelected || !selectAllTotalCount,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  // effects
  useEffect(() => {
    setStoredFilters(queryFilters);
    setQuery({
      filters: buildFilter(queryFilters),
      order: {
        orderDirection: sortBy[0].direction.toUpperCase() as OrderDirection,
        orderKey: sortBy[0].field as PagedLinjeQueryField,
      },
    });
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilters, setQuery, setStoredFilters, sortBy]);

  useEffect(() => {
    if (!fetchAllSelected || fetchAllLoading || !data) return;

    const selectedIds = data.fmulinjer.edges.map(
      (edge) => edge.node.id,
    ) as Array<string>;
    selectAll(selectedIds);
    setFetchAllSelected(false);
    setSelectAllTotalCount(null);
  }, [data, fetchAllLoading, fetchAllSelected, selectAll]);

  useEffect(() => {
    if (selectAllQueryError && fetchAllSelected) {
      setSelectAllError(selectAllQueryError);
      setFetchAllSelected(false);
      setSelectAllTotalCount(null);
    }
  }, [selectAllQueryError, fetchAllSelected]);

  // query
  const currentPage = usePagedLinjeQuery(
    {
      after: after,
      before: before,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );

  const nextPage = usePagedLinjeQuery(
    {
      after: currentPage.data?.fmulinjer?.pageInfo.endCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );

  const prevPage = usePagedLinjeQuery(
    {
      before: currentPage.data?.fmulinjer?.pageInfo.startCursor,
      fields: getActiveColumnFields(columns),
      filters: query.filters ?? "",
      order: query.order,
    },
    columns,
  );
  const loading = currentPage.loading || nextPage.loading || prevPage.loading;

  return (
    <PagedLinjeQueryContext.Provider
      value={{
        loading,
        error: currentPage.error,
        pagedResponse: currentPage?.data,
        columns,
        setActiveColumns,
        setQuery,
        query,
        page,
        nextPage,
        prevPage,
        type,
        refetch: currentPage.refetch,
        selectAllError,
        clearSelectAllError: () => setSelectAllError(undefined),
        selection: {
          addSelectedIds,
          clearSelection,
          removeSelectedIds,
          selectAll: (totalCount: number) => {
            setSelectAllError(undefined);
            setSelectAllTotalCount(totalCount);
            setFetchAllSelected(true);
          },
          selectedIds,
          lastSelectedId,
          setLastSelectedId,
        },
      }}
    >
      <LinjeFiltersContext.Provider value={queryFilters}>
        <LinjeFiltersEventContext.Provider value={updateFilters}>
          {children}
        </LinjeFiltersEventContext.Provider>
      </LinjeFiltersContext.Provider>
    </PagedLinjeQueryContext.Provider>
  );
};

function usePagedLinjeQuery(
  query: PagedLinjeQuery,
  columns: IColumn<PagedLinjeQueryField>[],
) {
  return useQuery<PagedLinjeResponse>(
    GET_PAGED_LINJE_QUERY({
      after: query.after,
      before: query.before,
      fields: getActiveColumnFields(columns),
      filters: query.filters,
      order: query.order,
    }),
  );
}

export function defaultSetSelected(
  rowData: IRowData,
  selected: boolean,
  shiftKeyPressed: boolean,
  edges: any,
  lastSelectedId: string | undefined,
  addSelectedIds: (ids: string[]) => void,
  removeSelectedIds: (ids: string[]) => void,
  setLastSelectedId: (id: string) => void,
) {
  const { id } = rowData;
  const ids = [id];
  if (shiftKeyPressed && lastSelectedId) {
    const index = edges.findIndex((e) => e.node.id === id);
    const lastIndex = edges.findIndex((e) => e.node.id === lastSelectedId);
    const start = Math.min(index, lastIndex);
    const end = Math.max(index, lastIndex);
    const range = edges.slice(start, end + 1);
    ids.push(
      ...range.map((r) => {
        return r.node.id;
      }),
    );
  }

  if (selected) addSelectedIds(ids);
  else removeSelectedIds(ids);
  setLastSelectedId(id);
}

const defaultSortBy: Array<SortBy<PagedLinjeQueryField>> = [
  {
    field: "nr",
    direction: "desc",
  },
];
